import { composeWithDevTools } from '@redux-devtools/extension';
import { createStore, applyMiddleware } from 'redux';
import thunkMiddleWare from 'redux-thunk';
import axios from 'axios';
import { INIT, INPUT, NAVIGATE, RESET, SYNC, BUSY, BROKEN, UPDATE } from '../../shared/wizard/actions';
import initState from './config';
import {triggerScroll} from "shared/helpers";

const framed = location.pathname.split('/')[2] === 'framed';
initState.framed = framed;

let storage;

axios.interceptors.response.use(null, error => new Promise((resolve, reject) => {
  if (error.response.status !== 422) {
    storage.dispatch({ type: BROKEN });
  }
  return reject(error);
}));

const validate = (page, state, callback) => {
  axios({
    method: 'post',
    url: `/hns/calculate/${page}`,
    data: Object.keys(state.input).reduce((mem, key) => {
      const v = state.input[key];
      if (v && typeof v.value !== 'undefined') mem[key] = v.value;
      else mem[key] = v;
      return mem;
    }, {}),
  }).then((res) => {
    if (res.data && res.data.length === 0) res.data = {};
    storage.dispatch({ type: SYNC, recalculate: null, payload: res.data });
    callback();
  }).catch((error) => {
    console.error(error);
    error.response = error.response || {};
    const { errors } = error.response.data || {};
    storage.dispatch({ type: SYNC, syncing: false, payload: errors ? { errors } : {} });
  });
};

const navigate = (state, action) => {
  if (state.syncing) return state;

  const n = action.delta || 0; // n == 1 returns the next page route

  const { page } = action.location.state || { page: state.pages[0] };

  let index = typeof action.index === 'number' ? action.index : state.pages.indexOf(page);

  if (index === -1) index = 0;

  index += n;

  const newPage = state.pages[index] || state.pages[0];

  const update = `/${state.app}${state.framed ? '/framed' : ''}/${newPage + action.location.search}`;

  const requireValidation =
    n > -1 &&
    newPage !== state.pages[0] &&
    page !== state.pages.slice(-1)[0] &&
    page !== 'results';

  const done = () => {
    action.navigate(update, { state: { page: newPage }});
    triggerScroll();
  }

  if (requireValidation) {
    validate(page, state, () => {
      if (!state.pages[index - 1] || page === state.pages[index - 1]) return done();
      return validate(state.pages[index - 1], state, done);
    });
  } else done();

  return {
    ...state,
    syncing: state.syncing || requireValidation,
    recalculating: typeof action.recalculate !== 'undefined' ?
      action.recalculate : state.recalculating,
  };
};

const reset = (state, action) => {
  action.navigate(`/${state.app}${state.framed ? '/framed' : ''}/${action.location.search}`, { state: { page: state.pages[0] }});
  return {
    ...initState,
    syncing: false,
  };
};

const busy = state => ({
  ...state,
  syncing: true,
});

const input = (state, action) => ({
  ...state,
  input: {
    ...state.input,
    ...action.payload,
  },
});

const sync = (state, action) => ({
  ...state,
  syncing: typeof action.syncing === 'boolean' ? action.syncing : state.syncing,
  recalculating: typeof action.recalculate !== 'undefined' ?
    action.recalculate : state.recalculating,
  output: {
    ...state.output,
    errors: {},
    ...action.payload,
  },
});

const init = (state, action) => {
  const settings = { ...state.settings, ...action.payload.settings };
  initState.settings = settings;
  initState.pages = action.payload.pages || state.pages;
  return { ...state, ...action.payload, settings };
};

function reducer(state, action) {
  switch (action.type) {
  case INIT: return init(state, action);
  case RESET: return reset(state, action);
  case INPUT: return input(state, action);
  case BUSY: return busy(state);
  case SYNC: return sync(state, action);
  case NAVIGATE: return navigate(state, action);
  case BROKEN: return { ...state, broken: true };
  case UPDATE: return { ...state, ...action.payload };
  default: return state;
  }
}

export const enhancer = composeWithDevTools(applyMiddleware(thunkMiddleWare));

export const store = createStore(reducer, initState, enhancer);

storage = store;
